import React, { useState, useRef, useEffect } from 'react';

export default function Dropdown({
    options,
    value,
    onChange,
    placeholder,
    disabled
}) {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const containerRef = useRef(null);
    const listRef = useRef(null);
    const searchTimeout = useRef(null);

    const selected = options.find((opt) => opt.value === value);

    // Close on outside click
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                containerRef.current &&
                !containerRef.current.contains(event.target)
            ) {
                setIsOpen(false);
                setSearchTerm('');
            }
        };

        document.addEventListener('mousedown', handleClickOutside);

        return () =>
            document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Focus dropdown when opened
    useEffect(() => {
        if (isOpen) {
            listRef.current?.focus();
        }
    }, [isOpen]);

    const toggleOpen = () => {
        if (disabled) return;
        setIsOpen((prev) => !prev);
    };

    const handleSelect = (option) => {
        onChange(option.value);
        setIsOpen(false);
        setSearchTerm('');
    };

    const handleTriggerKeyDown = (event) => {
        if (event.key === 'Escape') {
            setIsOpen(false);
        } else if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            toggleOpen();
        } else if (event.key === 'ArrowDown') {
            event.preventDefault();
            if (!isOpen) setIsOpen(true);
        }
    };

    // Type-to-search
    const handleListKeyDown = (event) => {
        if (event.key === 'Escape') {
            setIsOpen(false);
            return;
        }

        if (event.key.length !== 1) return;

        const term = (searchTerm + event.key).toLowerCase();
        setSearchTerm(term);

        const match = options.find((option) =>
            option.label.toLowerCase().startsWith(term)
        );

        if (match) {
            onChange(match.value);

            // Scroll selected item into view
            const element = document.getElementById(
                `dropdown-option-${match.value}`
            );
            element?.scrollIntoView({
                block: 'nearest'
            });
        }

        clearTimeout(searchTimeout.current);

        searchTimeout.current = setTimeout(() => {
            setSearchTerm('');
        }, 700);
    };

    return (
        <div className="dropdown" ref={containerRef}>
            <button
                type="button"
                className={
                    'dropdown__trigger' +
                    (isOpen ? ' dropdown__trigger--open' : '') +
                    (selected ? ' dropdown__trigger--filled' : '')
                }
                onClick={toggleOpen}
                onKeyDown={handleTriggerKeyDown}
                disabled={disabled}
                aria-haspopup="listbox"
                aria-expanded={isOpen}
            >
                <span>{selected ? selected.label : placeholder}</span>

                <svg
                    className="dropdown__chevron"
                    viewBox="0 0 12 7"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden="true"
                >
                    <path
                        d="M1 1L6 6L11 1"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                </svg>
            </button>

            {isOpen && (
                <ul
                    ref={listRef}
                    className="dropdown-menu"
                    role="listbox"
                    tabIndex={0}
                    onKeyDown={handleListKeyDown}
                >
                    {options.map((option) => {
                        const isSelected = option.value === value;

                        return (
                            <li key={option.value}>
                                <button
                                    id={`dropdown-option-${option.value}`}
                                    type="button"
                                    className={
                                        'dropdown-menu__item' +
                                        (isSelected
                                            ? ' dropdown-menu__item--selected'
                                            : '')
                                    }
                                    onClick={() => handleSelect(option)}
                                    role="option"
                                    aria-selected={isSelected}
                                >
                                    <span className="dropdown-menu__item__check">
                                        {isSelected && (
                                            <svg
                                                width="14"
                                                height="14"
                                                viewBox="0 0 14 14"
                                                fill="none"
                                                xmlns="http://www.w3.org/2000/svg"
                                            >
                                                <path
                                                    d="M2.5 7L5.5 10L11.5 3.5"
                                                    stroke="currentColor"
                                                    strokeWidth="1.6"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                />
                                            </svg>
                                        )}
                                    </span>

                                    <span>{option.label}</span>
                                </button>
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
}